import { useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { PrimaryButton } from 'shared/components/button'
import FormInput from 'shared/components/form-input'
import { BadRequest } from 'shared/errors/bad-request'
import { login } from '../api/community-api'
import { useJoinCommunity } from '../api/use-join-community'
import { useAcceptInvitationFlow } from '../members/hooks/use-accept-invitation-flow'

interface UserLoginFormInterface {
  email: string
  password: string
}

const defaultErrors: UserLoginFormInterface = {
  email: '',
  password: '',
}

const defaultLoginData: UserLoginFormInterface = {
  email: '',
  password: '',
}

function LoginForm(): ReactNode {
  const { t } = useTranslation()
  const router = useRouter()
  const { confirmationToken, invitationToken } = router.query
  const communityPath = router.query.communityPath ?? ''
  const joinCommunityMutation = useJoinCommunity()
  const acceptInvitation = useAcceptInvitationFlow({ communityPath })
  const [errors, setErrors] = useState(defaultErrors)
  const [commonError, setCommonError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [loginData, setLoginData] = useState(defaultLoginData)

  async function submit(): Promise<void> {
    if (!communityPath) return
    // TODO: remake to use TanStack Query
    try {
      setIsLoading(true)
      await login(loginData.email, loginData.password)

      if (invitationToken) {
        try {
          await acceptInvitation({
            invitationToken,
            confirmationToken: confirmationToken ?? undefined,
          })
        } catch {
          setIsLoading(false)
          setCommonError(t('core.error.internal_error_message'))
        }
        return
      }

      await joinCommunityMutation.mutateAsync(communityPath)
      globalThis.location.replace(`/community/${communityPath}`)
    } catch (error) {
      setIsLoading(false)
      if (error instanceof BadRequest) {
        if (error.errors.fields) {
          setErrors({
            email: error.errors.fields.email?.join('\n'),
            password: error.errors.fields.password?.join('\n'),
          })
        } else if (error.errors.common) {
          setCommonError(error.errors.common.join('\n'))
        }
      }
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <FormInput
        type="email"
        value={loginData.email}
        className="flex-auto"
        label={t('global.email_address')}
        onChange={e => {
          setLoginData(prevState => ({ ...prevState, email: e.target.value }))
        }}
        maxLength={180}
        error={errors.email}
      />
      <FormInput
        type="password"
        value={loginData.password}
        className="flex-auto"
        label={t('global.password')}
        onChange={e => {
          setLoginData(prevState => ({
            ...prevState,
            password: e.target.value,
          }))
        }}
        maxLength={180}
        error={errors.password}
      />
      <PrimaryButton
        isLoading={isLoading}
        onClick={submit}
        disabled={!loginData.email || !loginData.password || isLoading}
      >
        {t('join_form.button.login.title')}
      </PrimaryButton>
      {commonError && <p className="mt-2 text-center text-sm text-danger">{commonError}</p>}
    </div>
  )
}

export default LoginForm
